import { DoorClosed, DoorClosedLocked, Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import ElectronicTable from "./electronic-table";
import PushButtonTable from "./push-button-table";
import {
  getElectronicLockboxes,
  getPushButtonLockbox,
} from "../../utils/firebasefunctions";

function MainLockboxInventory() {
  const [activeTab, setActiveTab] = useState("electronic");
  const status = ["Available", "In Repair", "Lost"];
  const [electronicRows, setElectronicRows] = useState([]);
  const [pushButtonRows, setPushButtonRows] = useState([]);
  const [electronicData, setElectronicData] = useState([]);
  const [pushButtonData, setPushButtonData] = useState([]);
  const tabs = [
    { id: "electronic", label: "Electronic", icon: DoorClosedLocked },
    { id: "pushButton", label: "Push Button", icon: DoorClosed },
  ];
  const childRef = useRef();

  const currentData =
    activeTab === "electronic" ? electronicData : pushButtonData;

  const handleAddRow = () => {
    childRef.current.addRow();
  };

  useEffect(() => {
    loadData();
    loadStats();
  }, []);

  const loadData = async () => {
    const [electronic, pushbutton] = await Promise.all([
      getElectronicLockboxes(),
      getPushButtonLockbox(),
    ]);

    setElectronicRows(electronic);
    setPushButtonRows(pushbutton);
  };

  const loadStats = async () => {
    try {
      const [electronic, pushbutton] = await Promise.all([
        getElectronicLockboxes(),
        getPushButtonLockbox(),
      ]);

      setElectronicData(electronic);
      setPushButtonData(pushbutton);
    } catch (error) {
      console.error("Stats loading error:", error);
    }
  };

  const totalCount = currentData.length;

  const getStatusCount = (data, status) => {
    return data.filter((r) => r.status === status).length;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">
          Lockbox Inventory
        </h3>
        <button
          className="bg-[#1E293B] text-white rounded-md font-medium hover:bg-[#0f172a] transition-colors duration-150 ease-in-out flex items-center gap-2 px-3 py-2"
          onClick={handleAddRow}
        >
          <Plus size={18} />
          Add Lockbox
        </button>
      </div>
      <div className="flex gap-2 bg-white p-2 rounded-2xl shadow overflow-x-auto scrollbar-hide">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
            }}
            className={`flex items-center justify-center gap-2 whitespace-nowrap px-4 py-2 rounded-lg font-semibold flex-shrink-0 transition-all ${
              activeTab === tab.id
                ? "bg-gradient-to-b from-[#0f172a] to-[#1E293B] text-white"
                : "bg-transparent text-gray-500 hover:bg-blue-50"
            }`}
          >
            {tab.icon && <tab.icon className="w-4 h-4" />}
            {tab.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-[2%] gap-y-3 lg:gap-x-0 lg:justify-between mt-6">
        <div className="w-[48%] md:w-[32%] lg:w-[18%] bg-white border border-black/10 rounded-[10px] px-[18px] py-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <div className="text-sm text-gray-500 uppercase tracking-[0.08em] font-semibold mb-1">
            Total
          </div>
          <div className="text-2xl font-bold text-slate-900 leading-none">
            {totalCount}
          </div>
        </div>
        <div className="w-[48%] md:w-[32%] lg:w-[18%] bg-white border border-black/10 rounded-[10px] px-[18px] py-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <div className="text-sm text-gray-500 uppercase tracking-[0.08em] font-semibold mb-1">
            Available
          </div>
          <div className="text-2xl font-bold text-[#047857] leading-none">
            {getStatusCount(currentData, "Available")}
          </div>
        </div>
        <div className="w-[48%] md:w-[32%] lg:w-[18%] bg-white border border-black/10 rounded-[10px] px-[18px] py-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <div className="text-sm text-gray-500 uppercase tracking-[0.08em] font-semibold mb-1">
            Assigned
          </div>
          <div className="text-2xl font-bold text-[#4338ca] leading-none">
            {getStatusCount(currentData, "Assigned")}
          </div>
        </div>
        <div className="w-[48%] md:w-[32%] lg:w-[18%] bg-white border border-black/10 rounded-[10px] px-[18px] py-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <div className="text-sm text-gray-500 uppercase tracking-[0.08em] font-semibold mb-1">
            In Repair
          </div>
          <div className="text-2xl font-bold text-[#b45309] leading-none">
            {getStatusCount(currentData, "In Repair")}
          </div>
        </div>
        <div className="w-[48%] md:w-[32%] lg:w-[18%] bg-white border border-black/10 rounded-[10px] px-[18px] py-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <div className="text-sm text-gray-500 uppercase tracking-[0.08em] font-semibold mb-1">
            Lost
          </div>
          <div className="text-2xl font-bold text-[#b91c1c] leading-none">
            {getStatusCount(currentData, "Lost")}
          </div>
        </div>
      </div>
      <div className="mt-6">
        {activeTab === "electronic" ? (
          <ElectronicTable
            ref={childRef}
            rows={electronicRows}
            setRows={setElectronicRows}
            status={status}
            onClose={loadStats}
            onStatsUpdate={loadStats}
          />
        ) : (
          <PushButtonTable
            ref={childRef}
            rows={pushButtonRows}
            setRows={setPushButtonRows}
            status={status}
            onClose={loadStats}
            onStatsUpdate={loadStats}
          />
        )}
      </div>
    </div>
  );
}

export default MainLockboxInventory;
